import { PenLine } from 'lucide-react';
import { TonePill } from '../tonePill';
import { cn } from '../ui/utils';

/**
 * NEW-11 trust surface: agent-drafted clinical artifacts (drafted notes, care
 * plans, plan revisions) must be unmistakably drafts until a clinician
 * confirms them — "the model says" is never allowed to read as "the record
 * says". Amber tone + pen icon + explicit text so the signal survives
 * color-blindness (safety rule: never color-only).
 */

/**
 * Backend tool names whose output is an agent-drafted artifact. This is the
 * registered-tool contract (march.tools manifest), not a text heuristic —
 * record-fetching tools like `get_care_plans` deliberately do NOT match.
 */
export const DRAFT_ARTIFACT_TOOLS: ReadonlySet<string> = new Set([
  'draft_note',
  'draft_plan',
  'revise_note',
]);

/** True when a message's tools_used indicates an agent-drafted artifact. */
export function hasDraftArtifactTools(toolsUsed: readonly string[] | undefined): boolean {
  return Boolean(toolsUsed?.some((tool) => DRAFT_ARTIFACT_TOOLS.has(tool)));
}

/** Compact inline pill for card headers (e.g. a care-plan card). */
export function DraftBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-flex shrink-0', className)}
      title="AI-generated draft — verify against the chart before acting."
    >
      <TonePill tone="amber" className="gap-1 whitespace-nowrap">
        <PenLine className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span>AI draft</span>
      </TonePill>
    </span>
  );
}

/**
 * Message-level strip shown above agent-drafted content.
 */
export function DraftAdvisoryStrip({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded border border-(--status-attention)/20 bg-status-attention-bg px-3 py-2.5 text-xs leading-snug text-status-attention-text',
        className,
      )}
    >
      <PenLine className="h-3.5 w-3.5 shrink-0 translate-y-px" aria-hidden="true" />
      <span>
        <span className="font-medium">AI draft</span> — not part of the record. Review and
        confirm before clinical use.
      </span>
    </div>
  );
}
